import {
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
  increment
} from 'firebase/firestore';
import { db } from '../firebase';
import { sendNotification } from './notificationService';
import {
  sendOrderConfirmationEmail,
  sendNewOrderNotificationToAdmin,
  sendOrderCancellationToAdmin,
  sendShippingNotificationEmail,
  sendInvoiceEmail
} from './emailService';
import {
  cancelPayment as cancelQNBPayment,
  refundPayment as refundQNBPayment
} from './qnbPaymentService';
import { createShipment, cancelShipment } from './suratKargoService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { createInvoice } from './kolaybiFaturaService';

// ==================== KOLEKSİYONLAR ====================
const ordersCollection = collection(db, 'Orders');
const customersCollection = collection(db, 'Customers');

const functions = getFunctions();
const createKolaybiInvoice = httpsCallable(functions, 'createKolaybiInvoice');

// ⚙️ AYARLAR
const ADMIN_EMAIL = 'info@tronapazarlama.com';

// 🔧 YARDIMCI FONKSİYON: Timestamp dönüşümü
const convertTimestamp = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  if (typeof timestamp === 'string') return new Date(timestamp);
  if (timestamp.seconds) return new Date(timestamp.seconds * 1000);
  return null;
};

// ==================== MÜŞTERİ OLUŞTUR/GÜNCELLE ====================
const createOrUpdateCustomer = async (orderData) => {
  try {
    const customerEmail = orderData.email ||
      orderData.customerEmail ||
      orderData.shippingAddress?.email ||
      null;

    const shippingAddress = orderData.shippingAddress || {};

    if (!customerEmail) {
      console.error('❌ Email bulunamadı! Müşteri oluşturulamıyor.');
      return null;
    }

    if (!shippingAddress.firstName || !shippingAddress.lastName) {
      console.error('❌ Ad/Soyad bulunamadı! Müşteri oluşturulamıyor.');
      return null;
    }

    const isGuest = orderData.customerType === 'guest' || orderData.customerId === 'guest';

    console.log('🔍 Müşteri kontrol ediliyor:', {
      email: customerEmail,
      isGuest: isGuest
    });

    const customerQuery = query(customersCollection, where('email', '==', customerEmail));
    const customerSnapshot = await getDocs(customerQuery);

    let customerId;

    if (customerSnapshot.empty) {
      console.log('📝 Yeni müşteri oluşturuluyor...');

      const newCustomer = {
        userId: isGuest ? null : (orderData.customerId || orderData.userId || null),
        customerType: isGuest ? 'guest' : 'member',
        email: customerEmail,
        name: `${shippingAddress.firstName} ${shippingAddress.lastName}`,
        phone: shippingAddress.phone || null,
        address: shippingAddress.address || null,
        city: shippingAddress.city || null,
        district: shippingAddress.district || null,
        zipCode: shippingAddress.zipCode || null,
        status: 'active',
        totalOrders: 1,
        totalSpent: orderData.total || 0,
        lastOrderDate: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        notes: isGuest ? 'Misafir müşteri' : '',
        tags: isGuest ? ['guest'] : []
      };

      const customerDocRef = await addDoc(customersCollection, newCustomer);
      customerId = customerDocRef.id;

      console.log(`✅ Yeni müşteri oluşturuldu (${isGuest ? 'Misafir' : 'Üye'}):`, customerId);
    } else {
      console.log('🔄 Mevcut müşteri güncelleniyor...');

      customerId = customerSnapshot.docs[0].id;
      const customerRef = doc(db, 'Customers', customerId);
      const currentData = customerSnapshot.docs[0].data();

      const updateData = {
        totalOrders: (currentData.totalOrders || 0) + 1,
        totalSpent: (currentData.totalSpent || 0) + (orderData.total || 0),
        lastOrderDate: serverTimestamp(),
        updatedAt: serverTimestamp(),
        phone: shippingAddress.phone || currentData.phone,
        address: shippingAddress.address || currentData.address,
        city: shippingAddress.city || currentData.city,
        district: shippingAddress.district || currentData.district
      };

      if (!isGuest && currentData.customerType === 'guest') {
        updateData.customerType = 'member';
        updateData.userId = orderData.customerId || orderData.userId;
        updateData.tags = (currentData.tags || []).filter(tag => tag !== 'guest');
        console.log('🔄 Misafir müşteri üye olarak güncellendi');
      }

      await updateDoc(customerRef, updateData);

      console.log('✅ Mevcut müşteri güncellendi:', customerId);
    }

    return customerId;
  } catch (error) {
    console.error('❌ Müşteri oluşturma/güncelleme hatası:', error);
    return null;
  }
};

// ==================== SİPARİŞ OLUŞTUR ====================
// orderService.js - createOrder fonksiyonu (GÜNCEL)

export const createOrder = async (orderData) => {
  try {
    const orderNumber = `ORD-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

    const isGuest = orderData.customerType === 'guest' || orderData.customerId === 'guest';

    console.log('📝 Sipariş oluşturuluyor:', {
      orderNumber: orderNumber,
      customerType: isGuest ? 'guest' : 'member',
      hasOrderIdInData: !!orderData.orderId
    });

    // Müşteri oluştur/güncelle
    const customerId = await createOrUpdateCustomer(orderData);

    const order = {
      orderNumber: orderNumber,
      customerId: isGuest ? 'guest' : (orderData.customerId || localStorage.getItem('userId') || 'anonymous'),
      customerType: isGuest ? 'guest' : 'member',
      firebaseCustomerId: customerId,
      customerName: `${orderData.shippingAddress.firstName} ${orderData.shippingAddress.lastName}`,
      customerEmail: orderData.email,
      customerPhone: orderData.shippingAddress.phone || null,
      ...orderData,
      status: 'pending',
      timeline: [{
        status: 'pending',
        action: 'Sipariş oluşturuldu',
        date: new Date().toISOString()
      }],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    console.log('📝 Firestore\'a kaydedilecek sipariş:', {
      orderId: order.orderId,
      orderNumber: order.orderNumber,
      customerId: order.customerId,
      customerType: order.customerType
    });

    const docRef = await addDoc(ordersCollection, order);

    // Stok güncellemesi
    const batch = writeBatch(db);
    for (const item of orderData.items) {
      const productRef = doc(db, 'Products', item.productId);
      batch.update(productRef, {
        stock: increment(-item.quantity),
        sales: increment(item.quantity)
      });
    }
    await batch.commit();

    console.log(`✅ Sipariş oluşturuldu (${isGuest ? 'Misafir' : 'Üye'}):`, {
      documentId: docRef.id,
      orderId: orderNumber,
      orderNumber: orderNumber
    });

    // Mail gönderimi
    try {
      await sendOrderConfirmationEmail({
        customerEmail: orderData.email,
        orderNumber: orderNumber,
        total: orderData.total,
        items: orderData.items,
        shippingAddress: orderData.shippingAddress,
        isGuest: isGuest
      });
      console.log('✅ Müşteriye sipariş onay maili gönderildi');
    } catch (mailError) {
      console.error('❌ Müşteri maili gönderilemedi:', mailError);
    }

    try {
      await sendNewOrderNotificationToAdmin({
        orderNumber: orderNumber,
        total: orderData.total,
        items: orderData.items,
        customerName: `${orderData.shippingAddress.firstName} ${orderData.shippingAddress.lastName}`,
        shippingAddress: orderData.shippingAddress,
        customerType: isGuest ? 'Misafir' : 'Üye'
      }, ADMIN_EMAIL);
      console.log('✅ Admin\'e yeni sipariş bildirimi gönderildi');
    } catch (mailError) {
      console.error('❌ Admin maili gönderilemedi:', mailError);
    }

    // ==================== E-FATURA OLUŞTURMA (KOLAYBİ API) ====================
    try {
      console.log('📄 ========================================');
      console.log('📄 E-FATURA OLUŞTURMA BAŞLIYOR');
      console.log('📄 ========================================');

      // ✅ Fatura adresi belirle
      const finalInvoiceAddress = orderData.useDifferentInvoiceAddress
        ? orderData.invoiceAddress
        : orderData.shippingAddress;

      console.log('📍 Final Invoice Address:', JSON.stringify(finalInvoiceAddress, null, 2));

      // ✅ KRITIK: Adres alanlarını kontrol et ve düzelt
      const addressForInvoice = {
        address: finalInvoiceAddress.address ||
          finalInvoiceAddress.fullAddress ||
          `${finalInvoiceAddress.street || ''} ${finalInvoiceAddress.neighborhood || ''}`.trim() ||
          'Adres Belirtilmedi',
        district: finalInvoiceAddress.district || 'Merkez',
        city: finalInvoiceAddress.city || 'İstanbul'
      };

      console.log('📍 Düzeltilmiş Address For Invoice:', JSON.stringify(addressForInvoice, null, 2));

      // ✅ Eksik alan kontrolü
      if (!addressForInvoice.address || addressForInvoice.address === 'Adres Belirtilmedi') {
        console.warn('⚠️ UYARI: Adres bilgisi eksik!');
      }
      if (!addressForInvoice.city) {
        console.error('❌ HATA: Şehir bilgisi zorunlu!');
        throw new Error('Şehir bilgisi eksik - Fatura oluşturulamıyor');
      }
      if (!addressForInvoice.district) {
        console.error('❌ HATA: İlçe bilgisi zorunlu!');
        throw new Error('İlçe bilgisi eksik - Fatura oluşturulamıyor');
      }

      // ✅ Müşteri bilgilerini Kolaybi API formatına göre hazırla
      let customerInfo;

      if (orderData.invoiceType === 'corporate') {
        console.log('🏢 KURUMSAL FATURA hazırlanıyor...');
        customerInfo = {
          companyName: orderData.invoiceInfo.companyName,
          taxNumber: orderData.invoiceInfo.taxNumber,
          taxOffice: orderData.invoiceInfo.taxOffice,
          email: orderData.email,
          phone: finalInvoiceAddress.phone,
        };
      } else {
        console.log('👤 BİREYSEL FATURA hazırlanıyor...');
        customerInfo = {
          firstName: finalInvoiceAddress.firstName,
          lastName: finalInvoiceAddress.lastName,
          name: finalInvoiceAddress.firstName,
          surname: finalInvoiceAddress.lastName,
          tcNo: orderData.invoiceInfo?.tcNo || "11111111110",
          email: orderData.email,
          phone: finalInvoiceAddress.phone,
        };
      }

      console.log('📋 Hazırlanan customerInfo:', JSON.stringify(customerInfo, null, 2));

      // ✅ Kolaybi API Payload
      const invoicePayload = {
        orderNumber,
        orderDate: new Date().toISOString().split("T")[0],
        customerType: orderData.invoiceType === "corporate" ? "corporate" : "individual",
        customerInfo,
        invoiceAddress: addressForInvoice, // ✅ Düzeltilmiş adres
        items: orderData.items.map((item) => ({
          productName: item.productName || item.name || item.title || "Ürün",
          quantity: Number(item.quantity),
          price: Number(item.price),
        })),
        shippingCost: Number(orderData.shippingCost || 0),
      };

      console.log("📤 KolayBi Payload:", JSON.stringify(invoicePayload, null, 2));

      // ✅ Kolaybi fonksiyonunu çağır
      const invoiceResult = await createInvoice(invoicePayload);

      console.log('✅ E-Fatura yanıtı:', invoiceResult);

      if (invoiceResult.success) {
        console.log('✅ E-Fatura başarılı:', invoiceResult.data.invoiceId);

        await updateDoc(docRef, {
          invoiceCreated: true,
          invoiceId: invoiceResult.data.invoiceId,
          associateId: invoiceResult.data.associateId,
          addressId: invoiceResult.data.addressId,
          grandTotal: invoiceResult.data.grandTotal,
          invoiceCreatedAt: serverTimestamp(),
          invoiceStatus: 'pending_gib_approval', // veya 'draft'
          invoiceNote: process.env.KOLAYBI_ENV === 'production'
            ? 'Fatura GİB onayı bekleniyor. UUID alındıktan sonra PDF indirilebilir.'
            : 'Test ortamı - Anında kullanılabilir'
        });

        console.log('✅ Sipariş belgesine fatura bilgisi eklendi');

        try {
          console.log('📧 Fatura maili gönderiliyor...');

          await sendInvoiceEmail({
            customerEmail: orderData.email,
            customerName: `${orderData.shippingAddress.firstName} ${orderData.shippingAddress.lastName}`,
            orderNumber: orderNumber,
            invoiceId: invoiceResult.data.invoiceId,
            invoiceNumber: invoiceResult.data.invoiceNumber || invoiceResult.data.invoiceId,
            // ⚠️ PDF URL henüz yok (fatura resmileştirilmemiş)
            // Şimdilik fatura ID'si ile portal linkini gönderelim
            invoicePortalUrl: `https://test-portal.noffix.com/invoices/${invoiceResult.data.invoiceId}`
          });

          console.log('✅ Fatura maili gönderildi');
        } catch (mailError) {
          console.error('❌ Fatura maili gönderilemedi:', mailError);
          // Mail hatası sipariş sürecini durdurmaz
        }

      } else {
        console.error('❌ E-Fatura oluşturulamadı:', invoiceResult.data?.error || invoiceResult.error);

        await updateDoc(docRef, {
          invoiceCreated: false,
          invoiceError: invoiceResult.data?.error || invoiceResult.error || 'Bilinmeyen hata',
          invoiceErrorAt: serverTimestamp()
        });
      }

    } catch (invoiceError) {
      console.error('❌ E-Fatura exception:', invoiceError);

      await updateDoc(docRef, {
        invoiceCreated: false,
        invoiceError: invoiceError.message,
        invoiceErrorAt: serverTimestamp()
      });
    }

    return {
      success: true,
      orderId: docRef.id,
      orderNumber
    };
  } catch (error) {
    console.error('Sipariş oluşturulamadı:', error);
    return { success: false, error: error.message };
  }
};

// ==================== 🚀 SİPARİŞİ KARGOYA VER ====================
export const shipOrder = async (orderId) => {
  try {
    console.log('📦 Sipariş kargoya veriliyor:', orderId);

    const orderRef = doc(db, 'Orders', orderId);
    const orderDoc = await getDoc(orderRef);

    if (!orderDoc.exists()) {
      return { success: false, error: 'Sipariş bulunamadı' };
    }

    const orderData = orderDoc.data();

    // ✅ Sipariş durumu kontrolü
    if (orderData.status === 'shipped') {
      return { success: false, error: 'Bu sipariş zaten kargoya verilmiş' };
    }

    if (orderData.status === 'cancelled') {
      return { success: false, error: 'İptal edilmiş sipariş kargoya verilemez' };
    }

    console.log('📦 Sürat Kargo\'ya gönderiliyor...');

    // ✅ DOĞRU: Tüm orderData'yı gönder
    const shipmentResult = await createShipment(orderData);

    // ❌ KARGO BAŞARISIZ
    if (!shipmentResult.success) {
      console.error('❌ Kargo oluşturulamadı:', shipmentResult.error);

      // Siparişe hata notu ekle (ama durumu değiştirme)
      const timeline = orderData.timeline || [];
      timeline.push({
        status: 'processing',
        action: 'Kargo oluşturma başarısız - Manuel işlem gerekli',
        date: new Date().toISOString(),
        error: shipmentResult.error
      });

      await updateDoc(orderRef, {
        cargoError: {
          message: shipmentResult.error,
          attemptedAt: new Date().toISOString(),
          needsManualShipping: true
        },
        timeline: timeline,
        updatedAt: serverTimestamp()
      });

      return {
        success: false,
        error: shipmentResult.error,
        requiresManualAction: true,
        message: 'Kargo oluşturulamadı. Sipariş "İşleniyor" durumunda kaldı. Manuel olarak kargoya verin.'
      };
    }

    // ✅ KARGO BAŞARILI
    console.log('✅ Kargo oluşturuldu:', shipmentResult.trackingNumber);

    const timeline = orderData.timeline || [];
    timeline.push({
      status: 'shipped',
      action: 'Sipariş kargoya verildi',
      date: new Date().toISOString(),
      trackingNumber: shipmentResult.trackingNumber,
      carrier: 'Sürat Kargo'
    });

    // Firestore güncelle
    await updateDoc(orderRef, {
      status: 'shipped',
      trackingNumber: shipmentResult.trackingNumber,
      shippedAt: serverTimestamp(),
      carrier: 'Sürat Kargo',
      cargoError: null, // Önceki hataları temizle
      timeline: timeline,
      updatedAt: serverTimestamp()
    });

    console.log('✅ Sipariş durumu güncellendi: shipped');

    // ✅ Müşteriye kargo bildirimi maili gönder
    try {
      const estimatedDelivery = new Date();
      estimatedDelivery.setDate(estimatedDelivery.getDate() + 3); // 3 gün sonra

      await sendShippingNotificationEmail({
        customerEmail: orderData.email,
        customerName: orderData.customerName,
        orderNumber: orderData.orderNumber,
        trackingNumber: shipmentResult.trackingNumber,
        carrier: 'Sürat Kargo',
        estimatedDelivery: estimatedDelivery.toISOString()
      });
      console.log('✅ Müşteriye kargo bildirimi gönderildi');
    } catch (mailError) {
      console.error('❌ Kargo bildirimi maili gönderilemedi:', mailError);
    }

    return {
      success: true,
      trackingNumber: shipmentResult.trackingNumber,
      carrier: 'Sürat Kargo',
      message: shipmentResult.mock
        ? '⚠️ TEST MODE: Sahte takip numarası oluşturuldu'
        : 'Sipariş başarıyla kargoya verildi'
    };

  } catch (error) {
    console.error('❌ Kargoya verme hatası:', error);
    return {
      success: false,
      error: error.message,
      requiresManualAction: true
    };
  }
};

// ==================== DİĞER FONKSİYONLAR ====================

export const getAllOrders = async () => {
  try {
    const q = query(ordersCollection, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);

    const orders = [];
    querySnapshot.forEach((document) => {
      const data = document.data();

      const orderData = {
        id: document.id,
        ...data,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
        deliveredAt: convertTimestamp(data.deliveredAt),
        cancelledAt: convertTimestamp(data.cancelledAt),
        refundedAt: convertTimestamp(data.refundedAt)
      };

      orders.push(orderData);
    });

    console.log(`✅ Toplam ${orders.length} sipariş yüklendi`);

    return { success: true, orders };
  } catch (error) {
    console.error('❌ Siparişler getirilirken hata:', error);
    return { success: false, error: error.message, orders: [] };
  }
};

export const getOrderById = async (orderId) => {
  try {
    const orderRef = doc(db, 'Orders', orderId);
    const snapshot = await getDoc(orderRef);

    if (!snapshot.exists()) {
      return { success: false, error: 'Sipariş bulunamadı' };
    }

    const data = snapshot.data();
    return {
      success: true,
      order: {
        id: snapshot.id,
        ...data,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
        deliveredAt: convertTimestamp(data.deliveredAt),
        cancelledAt: convertTimestamp(data.cancelledAt),
        refundedAt: convertTimestamp(data.refundedAt)
      }
    };
  } catch (error) {
    console.error('getOrderById error:', error);
    return { success: false, error: error.message };
  }
};

export const updateOrderStatus = async (orderId, newStatus) => {
  try {
    const orderRef = doc(db, 'Orders', orderId);
    const orderDoc = await getDoc(orderRef);

    if (!orderDoc.exists()) {
      return { success: false, error: 'Sipariş bulunamadı' };
    }

    const currentOrder = orderDoc.data();

    const timeline = currentOrder.timeline || [];
    timeline.push({
      status: newStatus,
      action: getStatusActionText(newStatus),
      date: new Date().toISOString(),
      updatedBy: 'Admin'
    });

    const updateData = {
      status: newStatus,
      timeline: timeline,
      updatedAt: serverTimestamp()
    };

    if (newStatus === 'delivered') {
      updateData.deliveredAt = serverTimestamp();
    }

    await updateDoc(orderRef, updateData);

    console.log('✅ Sipariş durumu güncellendi:', orderId, newStatus);

    return { success: true };
  } catch (error) {
    console.error('Sipariş durumu güncellenemedi:', error);
    return { success: false, error: error.message };
  }
};

// Helper fonksiyon
const getStatusActionText = (status) => {
  const actions = {
    pending: 'Sipariş beklemede',
    processing: 'Sipariş hazırlanıyor',
    shipped: 'Sipariş kargoya verildi',
    delivered: 'Sipariş teslim edildi',
    cancelled: 'Sipariş iptal edildi'
  };
  return actions[status] || status;
};

/**
 * Sipariş numarası ve email ile sipariş getir (Misafir takip için)
 * ✅ GÜVENLİK: Admin SDK kullanarak veya özel claim ile
 */
/**
 * Sipariş numarası ve email ile sipariş getir (Hem Üye Hem Misafir için)
 */
export const getOrderByNumberAndEmail = async (orderNumber, email) => {
  try {
    console.log('🔍 Sipariş sorgulanıyor:', { orderNumber, email });

    // ✅ Sadece orderNumber ile sorgula (email backend'de kontrol edilecek)
    const q = query(
      ordersCollection,
      where('orderNumber', '==', orderNumber),
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.log('❌ Sipariş numarası bulunamadı');
      return { success: false, error: 'Sipariş bulunamadı' };
    }

    // ✅ Client-side email kontrolü (hem üye hem misafir için)
    const matchingOrder = snapshot.docs.find(doc => {
      const data = doc.data();

      // Email kontrolü (customerEmail veya email alanlarından herhangi biri)
      const orderEmail = (data.customerEmail || data.email || '').toLowerCase();
      const searchEmail = email.toLowerCase().trim();

      const emailMatch = orderEmail === searchEmail;

      console.log('🔍 Email karşılaştırma:', {
        orderEmail,
        searchEmail,
        match: emailMatch,
        customerType: data.customerType
      });

      return emailMatch;
    });

    if (!matchingOrder) {
      console.log('❌ Email eşleşmiyor - Sipariş bulundu ama email hatalı');
      return {
        success: false,
        error: 'Sipariş bulunamadı veya e-posta adresi hatalı'
      };
    }

    const data = matchingOrder.data();

    const order = {
      id: matchingOrder.id,
      ...data,
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
      deliveredAt: convertTimestamp(data.deliveredAt),
      cancelledAt: convertTimestamp(data.cancelledAt),
      refundedAt: convertTimestamp(data.refundedAt)
    };

    console.log('✅ Sipariş bulundu:', {
      orderNumber: order.orderNumber,
      customerType: order.customerType,
      email: order.customerEmail || order.email
    });

    return {
      success: true,
      order: order
    };
  } catch (error) {
    console.error('❌ Sipariş getirme hatası:', error);
    return { success: false, error: error.message };
  }
};

export default {
  getAllOrders,
  getOrderById,
  updateOrderStatus,
  createOrder
};